'use client';

import Image from '@/components/Image';
import MagicJump from '@/components/MagicJump';
import { env } from '@/utils/env';
import { Autoplay, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { BannerData } from '../types';
import styles from './CampaignBanner.module.css';

interface CampaignBannerProps {
  data: BannerData;
  aspectRatio?: string;
  className?: string;
  contentClassName?: string;
  imageClassName?: string;
  width?: number;
  height?: number;
  bannerIndex?: number;
  onBannerClick?: (data: BannerData, index: number) => void;
}

interface CampaignBannerCarouselProps extends Omit<CampaignBannerProps, 'data'> {
  data: BannerData[];
  autoplayDelay?: number;
  isMobile?: boolean;
  pauseOnMouseEnter?: boolean;
  onActiveIndexChange?: (index: number) => void;
}

function resolveAssetUrl(path?: string): string {
  if (!path) return '';
  if (/^(https?:)?\/\//.test(path)) return path;
  return `${env.NEXT_PUBLIC_S3_URL}${path}`;
}

export default function CampaignBanner({
  data,
  aspectRatio,
  className = 'w-full',
  contentClassName = 'relative block h-full w-full',
  imageClassName = 'block h-auto w-full object-cover object-center',
  width = 1920,
  height = 1080,
  bannerIndex = 0,
  onBannerClick,
}: CampaignBannerProps) {
  if (!data?.image) {
    return null;
  }

  const alt = data.image_alt || 'activity banner';
  const content = (
    <div className={contentClassName}>
      <Image
        src={resolveAssetUrl(data.image)}
        alt={alt}
        width={width}
        height={height}
        className={imageClassName}
      />
    </div>
  );

  return (
    <div className={className} style={aspectRatio ? { aspectRatio } : undefined}>
      {data.jump_content?.Jump_target ? (
        <MagicJump
          item={data.jump_content}
          className="block h-full w-full cursor-pointer"
          aria-label={alt}
          onJumpClick={() => onBannerClick?.(data, bannerIndex)}
        >
          {content}
        </MagicJump>
      ) : (
        content
      )}
    </div>
  );
}

export function CampaignBannerCarousel({
  data,
  aspectRatio,
  className = 'w-full',
  contentClassName = 'relative block h-full w-full',
  imageClassName = 'block h-auto w-full object-cover object-center',
  width = 1920,
  height = 1080,
  autoplayDelay = 5000,
  isMobile = false,
  pauseOnMouseEnter = true,
  onActiveIndexChange,
  onBannerClick,
}: CampaignBannerCarouselProps) {
  const banners = data.filter((item) => item?.image);

  if (banners.length === 0) {
    return null;
  }

  if (banners.length === 1) {
    return (
      <CampaignBanner
        data={banners[0]}
        aspectRatio={aspectRatio || banners[0].aspectRatio}
        className={className}
        contentClassName={contentClassName}
        imageClassName={imageClassName}
        width={width}
        height={height}
        bannerIndex={0}
        onBannerClick={onBannerClick}
      />
    );
  }

  return (
    <Swiper
      className={`${className} ${styles.campaignBanner} ${isMobile ? styles.mobile : styles.desktop}`}
      style={aspectRatio ? { aspectRatio } : undefined}
      modules={[Autoplay, Pagination]}
      loop
      autoplay={{
        delay: autoplayDelay,
        disableOnInteraction: false,
        pauseOnMouseEnter,
      }}
      pagination={{ clickable: true }}
      observer
      observeParents
      onSwiper={(swiper) => onActiveIndexChange?.(swiper.realIndex || 0)}
      onSlideChange={(swiper) => onActiveIndexChange?.(swiper.realIndex || 0)}
    >
      {banners.map((banner, index) => (
        <SwiperSlide key={`${banner.image}-${index}`}>
          <CampaignBanner
            data={banner}
            aspectRatio={banner.aspectRatio}
            className="h-full w-full"
            contentClassName={contentClassName}
            imageClassName={imageClassName}
            width={width}
            height={height}
            bannerIndex={index}
            onBannerClick={onBannerClick}
          />
        </SwiperSlide>
      ))}
    </Swiper>
  );
}
